import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';

import {
    HIDDEN_HIRING_CLIENT_COLUMN,
    HIDDEN_HIRING_CLIENTS,
    QUERY_KEYS,
    SCORECARD_REPORT,
} from '../../../constants';
import { usePagination } from '../../../hooks';
import { setSelectedHiringClients } from '../../../redux';
import { popup } from '../../../utils';
import { Table } from '../Table';
import {
    getHiddenHiringClients,
    showHiringClients,
} from '../../../utils/apiServices/hideHiringClientApi';
import InputWithSearch from '../common/InputWithSearch';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { CircleSpinner } from '../../../assets/svg';

const UnhideHiringClientModal = ({ showModal, setShowModal, accountId }) => {
    const queryClient = useQueryClient();
    const [hiddenHClData, setHiddenHClData] = useState(undefined);
    const [search, setSearch] = useState('');
    const dispatch = useDispatch();
    const pagination = usePagination(hiddenHClData?.data.length, hiddenHClData?.count);
    const { selectedHiringClients, selectedAccount } = useSelector((s) => s.accountArray);

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.HIDDEN_HIRING_CLIENTS,
            accountId,
            debounceSearch,
            pagination.offset,
            pagination.limit,
        ],
        queryFn: async () =>
            await getHiddenHiringClients({
                accountId,
                offset: pagination.offset,
                limit: pagination.limit,
                search: debounceSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setHiddenHClData({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    const unHideHcMutation = useMutation({
        mutationFn: async () =>
            await showHiringClients({
                accountId: selectedAccount,
                hiringClientIds: selectedHiringClients,
            }),
        onSuccess: () => {
            popup('success', 'Hiring clients unhidden successfully');
            setShowModal(false);
            dispatch(setSelectedHiringClients([]));
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.HIRING_CLIENTS, accountId],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.HIDDEN_HIRING_CLIENTS, accountId],
            });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    return (
        <Modal
            show={showModal}
            onHide={() => {
                setShowModal(false);
                dispatch(setSelectedHiringClients([]));
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            fullscreen='sm-down'
        >
            <Modal.Header closeButton>
                <Modal.Title className='me-2' style={{ fontSize: '18px' }}>
                    {HIDDEN_HIRING_CLIENTS.MODAL.HEADING}
                </Modal.Title>
                <InputWithSearch
                    search={search}
                    setSearch={setSearch}
                    placeholder={SCORECARD_REPORT.SCORE_CARD_DETAILS_PAGE.SEARCH_BAR_PLACEHOLDER}
                    className={'w-50'}
                />
            </Modal.Header>
            <Modal.Body>
                <div className='modal-body p-0 pt-4'>
                    <Table
                        columns={HIDDEN_HIRING_CLIENT_COLUMN}
                        data={hiddenHClData?.data}
                        pagination={pagination}
                        tableStyle={{ height: '580px', overflow: 'auto' }}
                        stickyColumns={['Hiring Client']}
                        isLoading={isFetching}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex w-100 align-items-center justify-content-between modal-btn'>
                    <span
                        style={{ color: '#738da6', fontSize: '14px' }}
                    >{`${selectedHiringClients.length}/${hiddenHClData?.count} Selected`}</span>
                    <button
                        type='submit'
                        onClick={() => unHideHcMutation.mutate()}
                        className='ssc-primary-green-btn w-auto disabled-hover'
                        disabled={selectedHiringClients.length === 0 || unHideHcMutation.isPending}
                    >
                        {unHideHcMutation.isPending && (
                            <span className='me-1'>
                                <CircleSpinner />
                            </span>
                        )}
                        {HIDDEN_HIRING_CLIENTS.BUTTONS.SAVE}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default UnhideHiringClientModal;
